import { useState, useEffect, useCallback, useRef, Component, type ReactNode } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle, Radio, Phone, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { CallSessionDetail } from "@component/agent-calls/call-session-detail";
import { getAgentCallSession, updateSessionCategory } from "@service/agent";
import { CALL_CATEGORIES } from "@type/agent";
import type { CallSession, SessionStatus } from "@type/agent";

// Error boundary to catch render crashes in the detail view
class DetailErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; errorMsg: string }> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { hasError: false, errorMsg: "" };
    }
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, errorMsg: error?.message || "Unexpected error" };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="space-y-4">
                    <Link to="/agent/calls" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Call Log
                    </Link>
                    <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
                        <AlertCircle className="w-5 h-5" />
                        <span>Something went wrong rendering this session. {this.state.errorMsg}</span>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const POLL_STATUSES: SessionStatus[] = ["pending", "scheduled", "active", "analyzing"];
const POLL_INTERVAL = 5000;

const liveStatusConfig: Partial<Record<SessionStatus, { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bg: string; animate?: boolean }>> = {
    pending: { label: "Waiting to connect...", icon: Phone, color: "text-gray-600", bg: "bg-gray-50 border-gray-200", animate: true },
    scheduled: { label: "Call scheduled", icon: Phone, color: "text-indigo-600", bg: "bg-indigo-50 border-indigo-200" },
    active: { label: "Call in progress...", icon: Phone, color: "text-blue-600", bg: "bg-blue-50 border-blue-200", animate: true },
    analyzing: { label: "Call ended — analyzing results...", icon: Sparkles, color: "text-amber-600", bg: "bg-amber-50 border-amber-200", animate: true },
    results_ready: { label: "Call complete — results ready", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50 border-green-200" },
    failed: { label: "Call failed", icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
};

const LIVE_STATUSES = ["pending", "active", "analyzing"];

export default function AgentCallDetail() {
    const { id } = useParams<{ id: string }>();
    const [session, setSession] = useState<CallSession | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showStatusBanner, setShowStatusBanner] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!id) return;
        getAgentCallSession(id)
            .then((data) => {
                setSession(data);
                if (!data) setError("Session not found");
                if (data && POLL_STATUSES.includes(data.status)) {
                    setShowStatusBanner(true);
                }
            })
            .catch((err) =>
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load session."
                )
            )
            .finally(() => setLoading(false));
    }, [id]);

    // Poll for live sessions (pending/active/analyzing)
    useEffect(() => {
        if (!session || !id) return;
        if (!POLL_STATUSES.includes(session.status)) {
            // Clean up any existing poll if status became terminal
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }

        pollRef.current = setInterval(async () => {
            try {
                const updated = await getAgentCallSession(id, true);
                if (updated) {
                    setSession(updated);
                    if (!POLL_STATUSES.includes(updated.status)) {
                        setShowStatusBanner(true);
                        if (pollRef.current) {
                            clearInterval(pollRef.current);
                            pollRef.current = null;
                        }
                    }
                }
            } catch {
                // Silent — polling failure is non-critical
            }
        }, POLL_INTERVAL);

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [id, session?.status]);

    const isLive = session ? LIVE_STATUSES.includes(session.status) : false;

    const handleSessionUpdate = useCallback(async (updated: CallSession) => {
        const previous = session;
        setSession(updated);

        // Persist category change to backend
        if (updated.category && updated.category !== previous?.category) {
            try {
                await updateSessionCategory(updated.id, updated.category);
            } catch {
                // Revert on failure
                if (previous) setSession(previous);
            }
        }
    }, [session]);

    if (loading) {
        return (
            <div className="space-y-4">
                {/* Back link — kept live so the user can bail out without
                    waiting for the skeleton to clear. */}
                <Link
                    to="/agent/calls"
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Call Log
                </Link>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    {/* Header strip: contact + actions */}
                    <div className="p-6 flex items-start justify-between gap-4 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
                            <div className="space-y-2">
                                <div className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                <div className="h-3 w-40 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                            <div className="h-9 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                        </div>
                    </div>

                    {/* Meta row: date / duration / id / pills / score */}
                    <div className="px-6 py-4 flex flex-wrap items-center gap-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="h-4 w-36 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                        <div className="h-4 w-28 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                        <div className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                        <div className="h-6 w-20 bg-gray-100 dark:bg-gray-800 rounded-full animate-pulse" />
                        <div className="h-6 w-16 bg-gray-100 dark:bg-gray-800 rounded-full animate-pulse" />
                        <div className="h-6 w-20 bg-gray-100 dark:bg-gray-800 rounded-full animate-pulse" />
                        <div className="ml-auto h-6 w-12 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                    </div>

                    {/* Tabs */}
                    <div className="px-6 pt-4 flex gap-6 border-b border-gray-100 dark:border-gray-800">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-3" />
                        ))}
                    </div>

                    {/* Body: AI Summary + Key Findings/Risk + Next Actions */}
                    <div className="p-6 space-y-4">
                        {/* AI Summary card */}
                        <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10 p-5 space-y-2">
                            <div className="h-4 w-28 bg-blue-100 dark:bg-blue-900/40 rounded animate-pulse" />
                            <div className="h-3 w-full bg-blue-100 dark:bg-blue-900/40 rounded animate-pulse" />
                            <div className="h-3 w-11/12 bg-blue-100 dark:bg-blue-900/40 rounded animate-pulse" />
                            <div className="h-3 w-3/4 bg-blue-100 dark:bg-blue-900/40 rounded animate-pulse" />
                        </div>

                        {/* Key Findings + Risk Signals two-column */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-800 p-5 space-y-3">
                                    <div className="h-4 w-32 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    {Array.from({ length: 3 }).map((__, r) => (
                                        <div key={r} className="flex items-start gap-2">
                                            <div className="h-4 w-4 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse flex-shrink-0 mt-0.5" />
                                            <div className="h-3 flex-1 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* Next Actions list */}
                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-5 space-y-3">
                            <div className="h-4 w-32 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                            {Array.from({ length: 2 }).map((_, r) => (
                                <div key={r} className="flex items-center gap-2">
                                    <div className="h-4 w-4 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse flex-shrink-0" />
                                    <div className="h-3 flex-1 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                                    <div className="h-6 w-16 bg-gray-100 dark:bg-gray-800 rounded animate-pulse flex-shrink-0" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !session) {
        return (
            <div className="space-y-4">
                <Link
                    to="/agent/calls"
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Call Log
                </Link>
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error || "Session not found"}</span>
                </div>
            </div>
        );
    }

    const statusBanner = showStatusBanner ? liveStatusConfig[session.status] : null;

    return (
        <DetailErrorBoundary>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <Link
                        to="/agent/calls"
                        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Call Log
                    </Link>
                    {isLive && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                            <Radio className="w-3 h-3 animate-pulse" />
                            Live
                        </span>
                    )}
                </div>

                {/* Live status banner */}
                {statusBanner && (
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${statusBanner.bg}`}>
                        {statusBanner.animate ? (
                            <Loader2 className={`w-4 h-4 animate-spin ${statusBanner.color}`} />
                        ) : (
                            <statusBanner.icon className={`w-4 h-4 ${statusBanner.color}`} />
                        )}
                        <span className={`text-sm font-medium ${statusBanner.color}`}>
                            {statusBanner.label}
                        </span>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <CallSessionDetail
                        session={session}
                        callCategories={CALL_CATEGORIES}
                        onSessionUpdate={handleSessionUpdate}
                    />
                </div>
            </div>
        </DetailErrorBoundary>
    );
}
